/**
 * EngageAudiences — /engage/audiences
 * Shows the audience list and the builder modal.
 */
import React, { useState } from 'react';
import { Users, Plus, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast }  from 'sonner';
import { useAudiences } from '../context/AudienceContext';
import AudienceBuilderModal from '../components/engage/AudienceBuilderModal';
import AudienceCard         from '../components/engage/AudienceCard';

const EngageAudiences = () => {
  const { audiences, loading, deleteAudience } = useAudiences();
  const [builderOpen,     setBuilderOpen]     = useState(false);
  const [editingAudience, setEditingAudience] = useState(null);

  const openCreate = () => { setEditingAudience(null); setBuilderOpen(true); };
  const openEdit   = (a) => { setEditingAudience(a);   setBuilderOpen(true); };

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete audience "${a.name}"? This cannot be undone.`)) return;
    try {
      await deleteAudience(a.id);
      toast.success('Audience deleted');
    } catch (err) {
      toast.error('Failed to delete audience');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Audiences</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Define reusable contact segments to power campaigns and automations
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="text-white shrink-0"
          style={{ backgroundColor: 'var(--cb-orange)' }}
          data-testid="create-audience-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Audience
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : audiences.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-14 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-gray-300" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1.5">No audiences yet</h3>
          <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto leading-relaxed">
            Build an audience using filter rules — lead stage, service type, quote status, and more.
          </p>
          <Button
            onClick={openCreate}
            className="text-white"
            style={{ backgroundColor: 'var(--cb-orange)' }}
            data-testid="create-audience-empty-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Your First Audience
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="audience-list">
          {audiences.map((a) => (
            <AudienceCard key={a.id} audience={a} onEdit={openEdit} onDelete={handleDelete} />
          ))}
        </div>
      )}

      <AudienceBuilderModal
        isOpen={builderOpen}
        onClose={() => { setBuilderOpen(false); setEditingAudience(null); }}
        existingAudience={editingAudience}
      />
    </div>
  );
};

export default EngageAudiences;
